import { Box } from '@mui/material';
import Actionbuttons from 'components/common/actions-buttons';
import { sanitizeDescriptionRemovePayee } from 'components/ledger-entry-editor/utils/sanitize';
import React from 'react';
import { TFunction } from 'i18next';
import { RendererParams } from 'types/common';
import { capitalize, IntlCurrencyFormat } from 'utils/helpers';
import { AccountingType } from 'v2-types/order';

import { ColumnsType } from '../../../services';
import AccountedFor from '../components/accounted-for';
import SimpleCellText from '../components/simple-text-cell';

function isUuidV4(uuidString) {
  const uuidV4Regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
  return uuidString.match(uuidV4Regex) !== null;
}

export const processCode = (param: string) => {
  const stringParts = param.split('_');
  const stringFiltredParts = stringParts.map(f => {
    if (isUuidV4(f)) {
      return '<id>';
    }
    return f;
  });
  return stringFiltredParts.join('_');
};

export const ledgerColumns = (
  t: TFunction,
  handleEdit: (row: any) => void,
  handleViewParties: (row: any) => void,
  handleAddParty: (row: any) => void,
  handleDeleteLedger: (row: any) => void,
  handleCreateLink: (open: boolean) => void
): ColumnsType => ({
  parties: {
    header: {
      title: t('accounting:actions'),
      width: '128px',
      minWidth: '128px',
    },
    renderer: ({ row }) => {
      const enableDelte =
        row.kind.includes('manual') ||
        row.kind.includes('disbursement') ||
        row.kind.includes('receipt');
      return (
        <Actionbuttons
          handleAdd={() => {
            handleAddParty(row);
          }}
          handleParties={(e) => {
            e.stopPropagation();
            handleViewParties(row);
          }}
          handleEdit={(e) => {
            e.stopPropagation();
            handleEdit(row);
          }}
          handleDelete={
            enableDelte
              ? (e) => {
                e.stopPropagation();
                handleDeleteLedger(row);
              }
              : undefined
          }
          handleChain={(e) => {
            e.stopPropagation();
            handleCreateLink(row);
          }}
        />
      );
    },
  },
  kind: {
    header: {
      title: t('accounting:kind'),
      align: 'left',
      width: '100px',
      minWidth: '100px',
    },
    renderer: ({ row }: RendererParams) => (
      <SimpleCellText textAlign="left">{capitalize(row.kind)}</SimpleCellText>
    ),
  },
  code: {
    header: {
      title: t('accounting:code'),
      align: 'left',
      width: '150px',
      minWidth: '150px',
    },
    renderer: ({ row }: RendererParams) => (
      <SimpleCellText textAlign="left">{processCode(row.code)}</SimpleCellText>
    ),
  },
  description: {
    header: {
      title: t('accounting:description'),
      align: 'left',
    },
    renderer: ({ row }: RendererParams) => {
      const sanitizedDesc = sanitizeDescriptionRemovePayee(row?.description, row?.involved_parties);
      return (
        <SimpleCellText textAlign="left">{sanitizedDesc}</SimpleCellText>
      );
    },
  },
  amount: {
    header: {
      title: t('accounting:amount'),
      width: '100px',
      minWidth: '100px',
      align: 'right',
    },
    renderer: ({ row }: RendererParams) => {
      return row?.amount_faked > 0 ? (
        <Box>
          <SimpleCellText textAlign="right">{t('accounting:faked')}</SimpleCellText>
          <SimpleCellText textAlign="right">
            {IntlCurrencyFormat(parseFloat(row.amount_faked))}
          </SimpleCellText>
        </Box>
      ) : (
        <>
          <SimpleCellText textAlign="right">
            {IntlCurrencyFormat(parseFloat(row.amount))}
          </SimpleCellText>
        </>
      );
    },
  },
  letter: {
    header: {
      title: t('accounting:letter'),
      width: '90px',
      minWidth: '90px',
      align: 'center',
    },
    renderer: ({ row }: RendererParams) => (
      <SimpleCellText textAlign="center">{row?.letter}</SimpleCellText>
    ),
  },
  posted: {
    header: {
      title: t('accounting:posted'),
      width: '90px',
      minWidth: '90px',
      align: 'center',
    },
    renderer: ({ row }: RendererParams) => (
      <SimpleCellText textAlign="center">{t(`accounting:${row?.posted}`)}</SimpleCellText>
    ),
  },
  entryDate: {
    header: {
      title: t('accounting:entry-date'),
      width: '150px',
      minWidth: '150px',
    },
    renderer: ({ row }: RendererParams) => (
      <SimpleCellText textAlign="center">
        {(row?.entryDate && row?.entryDate.split('T')[0]) || ''}
      </SimpleCellText>
    ),
  },
  number: {
    header: {
      title: t('orders:numberHeader'),
      width: '71px',
      minWidth: '71px',
    },
    renderer: ({ row }: RendererParams) => (
      <SimpleCellText textAlign="center">{row?.number || ''}</SimpleCellText>
    ),
  },
  accountedFor: {
    header: {
      title: t('accounting:accounted-for'),
      width: '250px',
      minWidth: '250px',
    },
    renderer: ({ row }: RendererParams) => (
      <AccountedFor accountedInfo={row?.accounted_for || []} isFaked={row.amount_faked > 0} t={t} />
    ),
  },
});

export const parseLedgerColumns = (data: AccountingType[]) =>
  data.map((account) => ({
    id: account._id,
    code: account.code,
    description: account.description,
    amount: account.amount,
    amount_faked: account.amount_faked,
    letter: account.letter,
    involved_parties: account.involved_parties || [],
    kind: account.kind,
    amount_override: account.amount_override,
    entryDate: account.entry_date,
    amount_calculated: account.amount_calculated,
    number: account.number,
    months: account.months,
    posted: account.posted,
    accounted_for: account?.accounted_for || [],
  }));

export default {};
